import { Component, forwardRef, Input, inject } from '@angular/core';
import {
    AbstractControl,
    ControlValueAccessor,
    FormBuilder,
    FormControl,
    FormGroup,
    NG_VALIDATORS,
    NG_VALUE_ACCESSOR,
    ValidationErrors,
    Validator,
} from '@angular/forms';
import { Netzzuordnung } from '../../domain/model/netzzuordnung';
import { NetzzuordnungService } from '../../service/netzzuordnung/netzzuordnung.service';
import { ReplaySubject } from 'rxjs/internal/ReplaySubject';
import { lastValueFrom } from 'rxjs';

@Component({
    selector: 'tnb-netzzuordnung-form',
    templateUrl: './netzzuordnung-form.component.html',
    styleUrls: [
        './netzzuordnung-form.component.scss',
    ],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => NetzzuordnungFormComponent),
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => NetzzuordnungFormComponent),
            multi: true,
        },
    ],
    standalone: false,
})
export class NetzzuordnungFormComponent implements ControlValueAccessor, Validator {
    private fb = inject(FormBuilder);
    private netzzuordnungService = inject(NetzzuordnungService);

    public netzzuordnungForm: FormGroup = null;

    public recentNetzzuordnung: Netzzuordnung = null;
    public selectedNetzzuordnung: Netzzuordnung = null;
    public selectedNetzzuordnung$: ReplaySubject<Netzzuordnung> = new ReplaySubject<Netzzuordnung>(1);

    public ortsname = new FormControl({ value: '', disabled: true });
    public stationsname = new FormControl({ value: '', disabled: true });
    public trafoFeldName = new FormControl({ value: '', disabled: true });

    public ortsnamenSelection: string[] = [];
    public stationsNamenSelection: string[] = [];
    public trafoFeldNamenSelection: string[] = [];

    @Input() public stadtwerkeId: string;
    @Input() public disabled = false;

    constructor() {

        this.netzzuordnungForm = this.fb.group({
            ortsname: this.ortsname,
            stationsname: this.stationsname,
            trafoFeldName: this.trafoFeldName,
        });
    }

    updateDisabledStates() {
        if (this.disabled) {
            this.ortsname.disable();
            this.stationsname.disable();
            this.trafoFeldName.disable();
        } else {
            this.ortsname.enable();
            if (this.stationsNamenSelection.length === 1) {
                this.stationsname.disable();
            } else {
                this.stationsname.enable();
            }
            if (this.stationsname.value === null || this.trafoFeldNamenSelection.length < 2) {
                this.trafoFeldName.disable();
            } else {
                this.trafoFeldName.enable();
            }
        }
    }

    registerOnChange(fn: any): void {
        this.selectedNetzzuordnung$.subscribe(fn);
    }

    registerOnTouched(fn: any): void {
        this.selectedNetzzuordnung$.subscribe(fn);
    }

    registerOnValidatorChange(_fn: () => void): void {
    }

    setDisabledState(_isDisabled: boolean): void {
    }

    validate(_c: AbstractControl): ValidationErrors | null {
        if (this.selectedNetzzuordnung !== null) {
            return null;
        } else {
            return { required: true };
        }
    }


    writeValue(netzzuordnung: Netzzuordnung): void {
        this.recentNetzzuordnung = JSON.parse(JSON.stringify(netzzuordnung));
        this.initializeNetzzuordnung();
        if (netzzuordnung && netzzuordnung.deactivated) {
            this.netzzuordnungForm.controls['ortsname'].setErrors({ currentNetzzuordnungDeactivated: true });
        }
    }

    /* Start: Methods concerning the three Netzzuordnung drop downs */

    // initializes the three dependent fields of the Netzzuordnung
    private async initializeNetzzuordnung() {
        await this.fillOrtsnamen();
        await this.handleOrtsnameChanged(this.recentNetzzuordnung.ortsname);
        if (this.recentNetzzuordnung.stationsname !== null) {
            await this.handleStationsnameChanged(this.recentNetzzuordnung.stationsname);
            if (this.recentNetzzuordnung.trafoFeldName !== null) {
                await this.handleTrafoFeldNameChanged(this.recentNetzzuordnung.trafoFeldName);
            }
        }
    }

    // if a new Netzzuordnung.ortsname is selected, clear the other fields and load the available stationsnamen
    // if called from the template, set deleteCurrentNetzzuordnung to true, otherwise false, since we want to keep the
    // current Netzzuordnung when initializing the form for the very first time
    public async handleOrtsnameChanged(neuerOrtsname: string) {
        this.ortsname.setValue(neuerOrtsname);
        this.stationsname.setValue(null);
        this.resetTrafoFeldnameAndSelectedNetzzuordnung();
        await this.fillStationsnamen(neuerOrtsname);
        if (this.stationsNamenSelection.length === 1) {
            await this.handleStationsnameChanged(this.stationsNamenSelection[0]);
        }
        this.updateDisabledStates();
    }

    // if a new Netzzuordnung.stationsname is selected, clear the trafoFeldname field and load all available items
    public async handleStationsnameChanged(neuerStationsname: string) {
        this.stationsname.setValue(neuerStationsname);
        this.resetTrafoFeldnameAndSelectedNetzzuordnung();
        await this.fillTrafoFeldnamen(this.ortsname.value, neuerStationsname);
        if (this.trafoFeldNamenSelection.length === 0) {
            await this.handleTrafoFeldNameChanged('');
        } else if (this.trafoFeldNamenSelection.length === 1) {
            await this.handleTrafoFeldNameChanged(this.trafoFeldNamenSelection[0]);
        }
        this.updateDisabledStates();
    }

    // if a TrafoFeldname is selected, load the entity and assign it to the Erzeugungsanlage
    public async handleTrafoFeldNameChanged(neueTrafoFeldname: string) {
        this.trafoFeldName.setValue(neueTrafoFeldname);
        this.selectedNetzzuordnung = await lastValueFrom(this.netzzuordnungService
            .getFirstNetzzuordnung(this.getProperStadtwerkeId(), this.ortsname.value, this.stationsname.value, neueTrafoFeldname));
        this.selectedNetzzuordnung$.next(this.selectedNetzzuordnung);
        this.updateDisabledStates();
    }

    private resetTrafoFeldnameAndSelectedNetzzuordnung() {
        this.trafoFeldName.setValue(null);
        this.selectedNetzzuordnung = null;
        this.selectedNetzzuordnung$.next(null);
    }

    // fills the drop down list of Netzzuordnung.Ortsname with available items
    private async fillOrtsnamen() {
        this.ortsnamenSelection = await lastValueFrom(this.netzzuordnungService.getOrtsnamen(
            this.getProperStadtwerkeId(),
        ));
    }

    // fills the drop down list of Netzzuordnung.Stationsname with available items depending on the chose ortsname
    private async fillStationsnamen(ortsname: string) {
        this.stationsNamenSelection = await lastValueFrom(this.netzzuordnungService.getStationsnamen(
            this.getProperStadtwerkeId(), ortsname));
    }

    // fills the drop down list of Netzzuordnung.Stationsname with available items depending on the chose ortsname and stationsname
    private async fillTrafoFeldnamen(ortsname: string, stationsname: string) {
        this.trafoFeldNamenSelection = await lastValueFrom(this.netzzuordnungService.getTrafoFeldNamen(
            this.getProperStadtwerkeId(), ortsname, stationsname));
    }

    // assemebles a description of available details of this Netzzuordnung
    public assembleDescription(netzzuordnung: Netzzuordnung) {
        let name = netzzuordnung.ortsname + ', ' + netzzuordnung.stationsname;
        if (netzzuordnung.trafoFeldName) {
            name = name + ', ' + netzzuordnung.trafoFeldName;
        }
        return name;
    }

    // when the user is WESTNETZ, there is no stadtwerkeId passed via the @Input. In that case, however,
    // there must be a valid Netzzuordnung already present and thus, the stadtwerkeId can be obtained
    // via the related Netzbetreiber of the Netzzuordnung
    private getProperStadtwerkeId(): string {
        if (this.stadtwerkeId) {
            return this.stadtwerkeId;
        } else {
            if (this.recentNetzzuordnung) {
                return this.recentNetzzuordnung.netzbetreiber.stadtwerkeId;
            } else {
                return '';
            }
        }
    }

}
